import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, LogOut, FileText, HelpCircle, Star, FolderOpen, Trash2, Save, X, Pencil, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const TOKEN_KEY = "gm7_admin_token";
const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const inputCls = "w-full rounded-xl border border-gold/20 bg-navy-850/80 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-gold/60";

function formatApiErrorDetail(detail) {
  if (detail == null) return "خطایی رخ داد؛ دوباره تلاش کنید.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const applyResult = (promise, doneMessage) => promise.then(({ data }) => {
  toast.success(data.message || doneMessage);
}).catch((err) => {
  toast.error(formatApiErrorDetail(err.response?.data?.detail));
});

/* ---------- Post editor ---------- */

const PostEditor = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = initial.mode === "new";

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("عکس مقاله آپلود شد.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title?.trim() || !form.slug?.trim()) {
      toast.error("عنوان و نامک (slug) الزامی است.");
      return;
    }
    setSaving(true);
    const payload = {
      slug: form.slug.trim(),
      title: form.title,
      category: form.category || "عمومی",
      excerpt: form.excerpt || "",
      image: form.image || "",
      readMinutes: Number(form.readMinutes) || 5,
      date: form.date || "امروز",
      content: (form.contentText || "").split("\n").map((t) => t.trim()).filter(Boolean).map((t) => ({ text: t })),
    };
    if (isNew) await applyResult(api.post("/admin/posts", payload, authHeaders()), "مقاله منتشر شد.");
    else await applyResult(api.put(`/admin/posts/${initial.slug}`, payload, authHeaders()), "مقاله به‌روزرسانی شد.");
    setSaving(false);
    onDone();
  };

  return (
    <div className="glass-panel rounded-3xl border border-gold/30 p-6 sm:p-8" data-testid="admin-post-editor">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-gold">{isNew ? "مقاله جدید" : `ویرایش: ${initial.title}`}</h3>
        <button onClick={onCancel} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid="admin-post-editor-close"><X size={16} /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input value={form.title} onChange={set("title")} placeholder="عنوان مقاله *" className={inputCls} data-testid="admin-post-title-input" />
        <input value={form.slug} onChange={set("slug")} placeholder="نامک لاتین (مثلاً humanitarian-visa-news) *" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-post-slug-input" />
        <input value={form.category} onChange={set("category")} placeholder="دسته‌بندی (آمریکا، کانادا، استرالیا، شنگن، عمومی)" className={inputCls} data-testid="admin-post-category-input" />
        <input value={form.readMinutes} onChange={set("readMinutes")} placeholder="زمان مطالعه (دقیقه)" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-post-readtime-input" />
        <input value={form.date} onChange={set("date")} placeholder="تاریخ نمایش (مثلاً ۱۵ بهمن ۱۴۰۴)" className={inputCls} data-testid="admin-post-date-input" />
        <input value={form.image} onChange={set("image")} placeholder="لینک تصویر (اختیاری)" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-post-image-input" />
      </div>
      <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} placeholder="خلاصه مقاله" className={`${inputCls} mt-4`} data-testid="admin-post-excerpt-input" />
      <div className="mt-4 rounded-2xl border border-gold/20 bg-navy-850/60 p-4" data-testid="admin-post-image-upload-box">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-display text-sm font-bold text-slate-200">عکس مقاله</span>
          <label className="btn-ghost !cursor-pointer !px-5 !py-2 !text-xs">
            <Pencil size={13} />
            آپلود مستقیم عکس
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadImage} className="hidden" data-testid="admin-post-image-upload-input" />
          </label>
        </div>
        {form.image && (
          <div className="mt-3 flex items-center gap-3">
            <img src={form.image} alt="پیش‌نمایش عکس مقاله" className="h-20 w-32 rounded-xl border border-gold/25 object-cover" data-testid="admin-post-image-preview" />
            <p className="text-xs text-slate-500">عکس منتخب — با ذخیره مقاله منتشر می‌شود.</p>
          </div>
        )}
        <input value={form.image} onChange={set("image")} placeholder="یا لینک تصویر را دستی وارد کنید" dir="ltr" className={`${inputCls} mt-3 !py-2 text-left`} data-testid="admin-post-image-input" />
      </div>
      <textarea value={form.contentText} onChange={set("contentText")} rows={10} placeholder="متن مقاله — هر خط یک پاراگراف" className={`${inputCls} mt-4`} data-testid="admin-post-content-input" />
      <button onClick={save} disabled={saving} className="btn-gold mt-6 w-full disabled:opacity-60" data-testid="admin-post-save-button">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {isNew ? "انتشار زنده روی سایت" : "ذخیره تغییرات"}
      </button>
    </div>
  );
};

/* ---------- FAQ editor ---------- */

const FaqEditor = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = initial.mode === "new";

  const save = async () => {
    if (!form.q?.trim() || !form.a?.trim()) {
      toast.error("سوال و جواب هر دو الزامی است.");
      return;
    }
    setSaving(true);
    const payload = { category: form.category || "عمومی", q: form.q, a: form.a };
    if (isNew) await applyResult(api.post("/admin/faqs", payload, authHeaders()), "سوال جدید اضافه شد.");
    else await applyResult(api.put(`/admin/faqs/${initial.id}`, payload, authHeaders()), "سوال به‌روزرسانی شد.");
    setSaving(false);
    onDone();
  };

  return (
    <div className="glass-panel rounded-3xl border border-gold/30 p-6 sm:p-8" data-testid="admin-faq-editor">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-gold">{isNew ? "سوال جدید" : "ویرایش سوال"}</h3>
        <button onClick={onCancel} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid="admin-faq-editor-close"><X size={16} /></button>
      </div>
      <input value={form.category} onChange={set("category")} placeholder="دسته (عمومی، مالی، حقوقی)" className={inputCls} data-testid="admin-faq-category-input" />
      <input value={form.q} onChange={set("q")} placeholder="سوال *" className={`${inputCls} mt-4`} data-testid="admin-faq-question-input" />
      <textarea value={form.a} onChange={set("a")} rows={5} placeholder="پاسخ *" className={`${inputCls} mt-4`} data-testid="admin-faq-answer-input" />
      <button onClick={save} disabled={saving} className="btn-gold mt-6 w-full disabled:opacity-60" data-testid="admin-faq-save-button">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        ذخیره
      </button>
    </div>
  );
};

/* ---------- Testimonial editor ---------- */

const TestimonialEditor = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = initial.mode === "new";

  const save = async () => {
    if (!form.name?.trim() || !form.quote?.trim()) {
      toast.error("نام و متن نظر الزامی است.");
      return;
    }
    setSaving(true);
    const payload = { name: form.name, country: form.country || "-", visaType: form.visaType || "-", quote: form.quote, rating: Number(form.rating) || 5, date: form.date || "امروز" };
    if (isNew) await applyResult(api.post("/admin/testimonials", payload, authHeaders()), "نظر جدید اضافه شد.");
    else await applyResult(api.put(`/admin/testimonials/${initial.id}`, payload, authHeaders()), "نظر به‌روزرسانی شد.");
    setSaving(false);
    onDone();
  };

  return (
    <div className="glass-panel rounded-3xl border border-gold/30 p-6 sm:p-8" data-testid="admin-testimonial-editor">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-gold">{isNew ? "نظر موفق جدید" : `ویرایش نظر: ${initial.name}`}</h3>
        <button onClick={onCancel} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid="admin-testimonial-editor-close"><X size={16} /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input value={form.name} onChange={set("name")} placeholder="نام موکل *" className={inputCls} data-testid="admin-testimonial-name-input" />
        <input value={form.country} onChange={set("country")} placeholder="کشور" className={inputCls} data-testid="admin-testimonial-country-input" />
        <input value={form.visaType} onChange={set("visaType")} placeholder="نوع پرونده (مثلاً ویزای بشر دوستانه)" className={inputCls} data-testid="admin-testimonial-visa-input" />
        <input value={form.date} onChange={set("date")} placeholder="تاریخ (مثلاً دی ۱۴۰۴)" className={inputCls} data-testid="admin-testimonial-date-input" />
        <select value={form.rating} onChange={set("rating")} className={inputCls} data-testid="admin-testimonial-rating-select">
          {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r} className="bg-navy-900">{r} ستاره</option>)}
        </select>
      </div>
      <textarea value={form.quote} onChange={set("quote")} rows={5} placeholder="متن نظر موفق *" className={`${inputCls} mt-4`} data-testid="admin-testimonial-quote-input" />
      <button onClick={save} disabled={saving} className="btn-gold mt-6 w-full disabled:opacity-60" data-testid="admin-testimonial-save-button">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        ذخیره
      </button>
    </div>
  );
};

/* ---------- Case editor ---------- */

const CaseEditor = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = initial.mode === "new";

  const save = async () => {
    if (!form.code?.trim() || !form.clientName?.trim()) {
      toast.error("کد رهگیری و نام موکل الزامی است.");
      return;
    }
    setSaving(true);
    const payload = {
      code: form.code.trim(),
      clientName: form.clientName,
      country: form.country || "-",
      visaType: form.visaType || "-",
      lawyer: form.lawyer || "دکتر محمد احمدی",
      currentStep: Number(form.currentStep) || 1,
      percent: Math.min(100, Math.max(0, Number(form.percent) || 0)),
      steps: (form.stepsText || "").split("\n").map((t) => t.trim()).filter(Boolean),
      updates: (form.updatesText || "").split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
        const [date = "—", title = "", state = ""] = line.split("|").map((p) => p.trim());
        return { date, title, done: state.includes("انجام") };
      }),
    };
    if (isNew) await applyResult(api.post("/admin/cases", payload, authHeaders()), "پرونده جدید ایجاد شد.");
    else await applyResult(api.put(`/admin/cases/${initial.code}`, payload, authHeaders()), "پرونده به‌روزرسانی شد.");
    setSaving(false);
    onDone();
  };

  return (
    <div className="glass-panel rounded-3xl border border-gold/30 p-6 sm:p-8" data-testid="admin-case-editor">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-gold">{isNew ? "پرونده جدید" : `ویرایش پرونده ${initial.code}`}</h3>
        <button onClick={onCancel} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid="admin-case-editor-close"><X size={16} /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input value={form.code} onChange={set("code")} placeholder="کد رهگیری (مثلاً GM7-2026-55) *" dir="ltr" disabled={!isNew} className={`${inputCls} text-left disabled:opacity-50`} data-testid="admin-case-code-input" />
        <input value={form.clientName} onChange={set("clientName")} placeholder="نام موکل *" className={inputCls} data-testid="admin-case-client-input" />
        <input value={form.country} onChange={set("country")} placeholder="کشور" className={inputCls} data-testid="admin-case-country-input" />
        <input value={form.visaType} onChange={set("visaType")} placeholder="نوع پرونده" className={inputCls} data-testid="admin-case-visa-input" />
        <input value={form.currentStep} onChange={set("currentStep")} placeholder="مرحله جاری (عدد)" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-case-step-input" />
        <input value={form.percent} onChange={set("percent")} placeholder="درصد پیشرفت (۰ تا ۱۰۰)" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-case-percent-input" />
      </div>
      <input value={form.lawyer} onChange={set("lawyer")} placeholder="وکیل مسئول" className={`${inputCls} mt-4`} data-testid="admin-case-lawyer-input" />
      <textarea value={form.stepsText} onChange={set("stepsText")} rows={5} placeholder="مراحل پرونده — هر خط یک مرحله" className={`${inputCls} mt-4`} data-testid="admin-case-steps-input" />
      <textarea value={form.updatesText} onChange={set("updatesText")} rows={6} placeholder="به‌روزرسانی‌ها — هر خط: تاریخ | عنوان | انجام شد یا در انتظار" className={`${inputCls} mt-4`} data-testid="admin-case-updates-input" />
      <button onClick={save} disabled={saving} className="btn-gold mt-6 w-full disabled:opacity-60" data-testid="admin-case-save-button">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        ذخیره
      </button>
    </div>
  );
};

/* ---------- Row helper ---------- */

const Row = ({ title, sub, onEdit, onDelete, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-gold/15 bg-navy-850 p-4" data-testid={testId}>
    <div className="min-w-0">
      <p className="truncate font-display text-sm font-bold text-slate-100">{title}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
    <div className="flex shrink-0 gap-2">
      <button onClick={onEdit} className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid={`${testId}-edit`}><Pencil size={14} /></button>
      <button onClick={onDelete} className="flex h-9 w-9 items-center justify-center rounded-full border border-red-400/30 text-red-300" data-testid={`${testId}-delete`}><Trash2 size={14} /></button>
    </div>
  </div>
);

/* ---------- Panel ---------- */

function AdminPanel({ onLogout }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("posts");
  const [editor, setEditor] = useState(null);

  const posts = useQuery({ queryKey: ["admin-posts"], queryFn: () => api.get("/posts") });
  const faqs = useQuery({ queryKey: ["faqs"], queryFn: () => api.get("/faqs") });
  const testimonials = useQuery({ queryKey: ["admin-testimonials"], queryFn: () => api.get("/admin/testimonials", authHeaders()) });
  const cases = useQuery({ queryKey: ["admin-cases"], queryFn: () => api.get("/admin/cases", authHeaders()) });

  const refresh = () => {
    ["admin-posts", "posts", "faqs", "admin-testimonials", "testimonials", "admin-cases"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const openEditPost = async (slug) => {
    try {
      const { data } = await api.get(`/posts/${slug}`);
      setEditor({ tab: "posts", data: {
        mode: "edit", slug, title: data.title, category: data.category, excerpt: data.excerpt,
        image: data.image, readMinutes: data.readMinutes, date: data.date,
        contentText: (data.content || []).map((c) => c.text).join("\n"),
      } });
    } catch {
      toast.error("دریافت متن مقاله ناموفق بود.");
    }
  };

  const del = (fn, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    applyResult(fn(), "انجام شد.").then(refresh);
  };

  const TABS = [
    { id: "posts", label: "مقالات وبلاگ", icon: FileText, tid: "admin-tab-posts" },
    { id: "faqs", label: "سوالات متداول", icon: HelpCircle, tid: "admin-tab-faqs" },
    { id: "testimonials", label: "نظرات موفقیت", icon: Star, tid: "admin-tab-testimonials" },
    { id: "cases", label: "پرونده‌های رهگیری", icon: FolderOpen, tid: "admin-tab-cases" },
  ];

  const editors = {
    posts: (d) => <PostEditor initial={d} onDone={() => { setEditor(null); refresh(); }} onCancel={() => setEditor(null)} />,
    faqs: (d) => <FaqEditor initial={d} onDone={() => { setEditor(null); refresh(); }} onCancel={() => setEditor(null)} />,
    testimonials: (d) => <TestimonialEditor initial={d} onDone={() => { setEditor(null); refresh(); }} onCancel={() => setEditor(null)} />,
    cases: (d) => <CaseEditor initial={d} onDone={() => { setEditor(null); refresh(); }} onCancel={() => setEditor(null)} />,
  };

  const listRowEditors = {
    posts: (p) => openEditPost(p.slug),
    faqs: (f) => setEditor({ tab: "faqs", data: { mode: "edit", id: f.id, category: f.category, q: f.q, a: f.a } }),
    testimonials: (t) => setEditor({ tab: "testimonials", data: { mode: "edit", id: t.id, name: t.name, country: t.country, visaType: t.visaType, quote: t.quote, rating: t.rating, date: t.date } }),
    cases: (c) => setEditor({ tab: "cases", data: {
      mode: "edit", code: c.code, clientName: c.clientName, country: c.country, visaType: c.visaType,
      lawyer: c.lawyer, currentStep: c.currentStep, percent: c.percent,
      stepsText: (c.steps || []).join("\n"),
      updatesText: (c.updates || []).map((u) => `${u.date} | ${u.title} | ${u.done ? "انجام شد" : "در انتظار"}`).join("\n"),
    } }),
  };

  const deleteFns = {
    posts: (slug) => del(api.delete(`/admin/posts/${slug}`, authHeaders()), "مقاله حذف شد."),
    faqs: (id) => del(api.delete(`/admin/faqs/${id}`, authHeaders()), "سوال حذف شد."),
    testimonials: (id) => del(api.delete(`/admin/testimonials/${id}`, authHeaders()), "نظر حذف شد."),
    cases: (code) => del(api.delete(`/admin/cases/${code}`, authHeaders()), "پرونده حذف شد."),
  };

  const newBtns = {
    posts: { mode: "new", title: "", slug: "", category: "عمومی", excerpt: "", image: "", readMinutes: 5, date: "امروز", contentText: "" },
    faqs: { mode: "new", category: "عمومی", q: "", a: "" },
    testimonials: { mode: "new", name: "", country: "آمریکا", visaType: "", quote: "", rating: 5, date: "امروز" },
    cases: { mode: "new", code: "GM7-2026-", clientName: "", country: "آمریکا", visaType: "ویزای بشر دوستانه (کیس مسیحیت)", lawyer: "دکتر محمد احمدی", currentStep: 1, percent: 5, stepsText: "ثبت و قرارداد\nساخت کیس مذهبی\nسابمیت پرونده\nبررسی و مصاحبه\nصدور ویزا", updatesText: "امروز | ثبت پرونده | انجام شد" },
  };

  const lists = {
    posts: { data: posts.data?.data, title: (p) => p.title, sub: (p) => `${p.category} · ${p.date}`, tid: (p) => `admin-post-row-${p.slug}`, key: (p) => p.slug, newLabel: "نوشتن مقاله جدید", newTid: "admin-new-post-button" },
    faqs: { data: faqs.data?.data, title: (f) => f.q, sub: (f) => f.category, tid: (f, i) => `admin-faq-row-${i + 1}`, key: (f, i) => i, newLabel: "سوال جدید", newTid: "admin-new-faq-button" },
    testimonials: { data: testimonials.data?.data, title: (t) => t.name, sub: (t) => `${t.country} · ${t.visaType}`, tid: (t, i) => `admin-testimonial-row-${i + 1}`, key: (t, i) => t.id || i, newLabel: "نظر موفق جدید", newTid: "admin-new-testimonial-button" },
    cases: { data: cases.data?.data, title: (c) => `${c.code} — ${c.clientName}`, sub: (c) => `${c.country} · ${c.percent}٪ پیشرفت`, tid: (c) => `admin-case-row-${c.code}`, key: (c) => c.code, newLabel: "پرونده رهگیری جدید", newTid: "admin-new-case-button" },
  };

  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 pt-32" data-testid="admin-panel">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-black">پنل مدیریت <span className="gold-text">گروه ۷</span></h1>
          <p className="mt-1 text-xs text-slate-500">مدیریت کامل مقالات، سوالات متداول، نظرات موفقیت و پرونده‌های رهگیری — تغییرات بلافاصله روی سایت اعمال می‌شود.</p>
        </div>
        <button onClick={onLogout} className="btn-ghost !px-5 !py-2 !text-xs" data-testid="admin-logout-button"><LogOut size={14} />خروج</button>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setEditor(null); }} className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-sm font-bold transition-all ${tab === t.id ? "bg-gold text-navy-950" : "border border-gold/25 text-slate-300"}`} data-testid={t.tid}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {editor ? (
        editors[editor.tab](editor.data)
      ) : (
        <div>
          <button onClick={() => setEditor({ tab, data: newBtns[tab] })} className="btn-gold mb-6" data-testid={lists[tab].newTid}><Pencil size={15} />{lists[tab].newLabel}</button>
          <div className="space-y-3">
            {(lists[tab].data ?? []).map((item, i) => (
              <Row
                key={lists[tab].key(item, i)}
                title={lists[tab].title(item)}
                sub={lists[tab].sub(item)}
                onEdit={() => listRowEditors[tab](item)}
                onDelete={() => deleteFns[tab](lists[tab].key(item, i))}
                testId={lists[tab].tid(item, i)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Admin() {
  const [token, setToken] = useState(getToken());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("خوش آمدید");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token)
    return (
      <section className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-32" data-testid="admin-login-page">
        <div className="glass-panel rounded-3xl border border-gold/25 p-8 sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <ShieldCheck size={28} className="text-gold" />
            <h1 className="font-display text-xl font-black">ورود مدیر — گروه ۷</h1>
          </div>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ایمیل مدیر" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-login-email-input" required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="رمز عبور" dir="ltr" className={`${inputCls} text-left`} data-testid="admin-login-password-input" required />
            <button type="submit" disabled={loggingIn} className="btn-gold w-full disabled:opacity-60" data-testid="admin-login-button">
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
              {loggingIn ? "در حال ورود..." : "ورود به پنل"}
            </button>
          </form>
        </div>
      </section>
    );

  return <AdminPanel onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}
